/**
 * Traduz erros técnicos (API, ARGINK Bridge, rede) em mensagens humanas para o
 * signatário — nunca expor stack/JSON cru na UI.
 *
 * Rodada 4 (diagnóstico/observabilidade): todo `HumanError` carrega um `traceId`
 * (código de suporte) — o eco do servidor (`body.trace_id`) quando presente,
 * senão o trace_id gerado localmente para aquela chamada (sempre existe, ver
 * `ApiError`/`AgentError` em api.ts/agent.ts). A UI exibe esse código de forma
 * discreta e copiável (ver `components/SupportCode.tsx`).
 */
import { ApiError } from './api';
import { AgentError } from './agent';
import { newTraceId } from './trace';
import { formatSaoPauloDateTime } from './format';

/**
 * D-034 — subconjunto dos erros de pré-checagem de certificado que oferecem a
 * ação "escolher outro certificado" (re-listagem no agente local). Presente só
 * quando `describeError` reconhece um desses 4 códigos em `body.error`.
 */
export type CertificateErrorKind =
  | 'certificate_expired'
  | 'certificate_not_yet_valid'
  | 'certificate_key_usage_invalid'
  | 'certificate_subject_mismatch';

export interface HumanError {
  title: string;
  detail: string;
  /** Código de suporte a exibir/copiar — sempre presente. */
  traceId: string;
  /** D-034 — presente só nos 4 erros de certificado acima; instrui a UI a
   * oferecer "escolher outro certificado" em vez de ação genérica. */
  certificateKind?: CertificateErrorKind;
  /** D-034 — `true` nos erros terminais que NÃO admitem retry de certificado
   * nem de assinatura (422 `signer_cpf_missing`, 410 `signer_erased`): a UI
   * deve mostrar a recusa e nada mais (sem botão de "escolher outro
   * certificado", sem "tentar assinar de novo"). */
  terminal?: boolean;
}

/**
 * D-034 — política de pré-checagem do certificado (fail-closed, sem conversão
 * de modalidade pela UI, ver docs/DECISIONS.md). Consumido SEMPRE pelo campo
 * `error` do corpo — NUNCA por parsing de `message` (é a LEI do contrato).
 *
 * Roda ANTES do switch por `status` de `describeError` e devolve `null` quando
 * não reconhece o código — nesse caso o fluxo cai no switch de sempre, sem
 * nenhuma mudança de comportamento (é assim que `certificate_revoked`/
 * `revocation_unavailable`, que já tinham tratamento, continuam intocados).
 */
function describeD034Error(err: ApiError, traceId: string): HumanError | null {
  const code = err.body?.error;
  if (!code) return null;

  const notAfter = err.body?.not_after;
  if (err.status === 422 && code === 'certificate_expired' && typeof notAfter === 'string') {
    const { date, time } = formatSaoPauloDateTime(notAfter);
    return {
      title: 'Certificado vencido',
      detail:
        `Este certificado expirou em ${date} às ${time} e não pode ser utilizado para gerar uma ` +
        'assinatura qualificada ICP-Brasil. Renove o certificado, selecione outro certificado válido ou ' +
        'escolha outra modalidade de assinatura, quando permitida.',
      traceId,
      certificateKind: 'certificate_expired',
    };
  }

  const notBefore = err.body?.not_before;
  if (err.status === 422 && code === 'certificate_not_yet_valid' && typeof notBefore === 'string') {
    const { date, time } = formatSaoPauloDateTime(notBefore);
    return {
      title: 'Certificado ainda não é válido',
      detail:
        `Este certificado ainda não é válido; passa a valer em ${date} às ${time} e não pode ser ` +
        'utilizado para gerar uma assinatura qualificada ICP-Brasil. Selecione outro certificado válido ' +
        'ou escolha outra modalidade de assinatura, quando permitida.',
      traceId,
      certificateKind: 'certificate_not_yet_valid',
    };
  }

  if (err.status === 422 && code === 'certificate_key_usage_invalid') {
    return {
      title: 'Certificado não permite assinatura digital',
      detail:
        'Este certificado não permite assinatura digital (finalidade de uso insuficiente). Selecione um ' +
        'certificado de assinatura válido (e-CPF).',
      traceId,
      certificateKind: 'certificate_key_usage_invalid',
    };
  }

  if (err.status === 422 && code === 'certificate_subject_mismatch') {
    return {
      // NUNCA exibir CPF/dado do titular aqui — nem no título, nem no detalhe.
      title: 'Certificado não pertence a este signatário',
      detail:
        'Este certificado não pertence ao signatário convidado para esta assinatura. Selecione o seu ' +
        'próprio certificado.',
      traceId,
      certificateKind: 'certificate_subject_mismatch',
    };
  }

  if (err.status === 422 && code === 'signer_cpf_missing') {
    return {
      title: 'Cadastro do signatário incompleto',
      detail:
        // "envelope" na tela do signatário — o quinto e último, achado pela matriz que cruza
        // status COM código de erro (a antiga só mandava o status, e este ramo exige
        // `signer_cpf_missing` para existir).
        'Falta o CPF do signatário no cadastro deste envio, e sem ele não é possível confirmar o ' +
        'vínculo com o certificado antes de assinar. Peça para quem enviou o documento corrigir o ' +
        'cadastro e reenviar o convite.',
      traceId,
      terminal: true,
    };
  }

  if (err.status === 410 && code === 'signer_erased') {
    return {
      title: 'Signatário removido',
      detail:
        'Os dados deste signatário foram apagados (solicitação de eliminação de dados atendida) e esta ' +
        'cerimônia de assinatura não pode mais ser retomada.',
      traceId,
      terminal: true,
    };
  }

  if (err.status === 500 && code === 'artifact_validation_failed') {
    return {
      // #99 — a recusa vem da validação de segurança do SERVIÇO (pós-selagem),
      // não do computador do signatário: repetir o fluxo tende a falhar igual.
      // Nunca prometer que "tentar novamente" resolve, nem falar de cobrança
      // (vocabulário do remetente, não de quem assina).
      title: 'A assinatura não pôde ser concluída',
      detail:
        'O serviço recusou a conclusão ao validar o documento assinado — o problema está do lado do ' +
        'serviço, não do seu computador, e tentar de novo tende a falhar da mesma forma. Avise quem ' +
        'enviou o documento e informe o código de suporte abaixo.',
      traceId,
    };
  }

  return null;
}

/**
 * Log técnico no console — só para diagnóstico (status/kind/err_class/trace_id).
 * NUNCA inclui token/SST/DTBS/payload nem o `body` inteiro (podem crescer e
 * carregar campos sensíveis no futuro sem que este arquivo saiba).
 */
function logTechnical(
  source: 'api' | 'agent' | 'unknown',
  info: {
    status: number | null;
    kind?: string;
    errCode?: string | null;
    errClass?: string | null;
    /** Mensagem que o CLIENTE construiu (nunca a do servidor, nunca o `body`). Ela deixou de
     * ir para a tela no M6-A por carregar jargão; precisa continuar chegando a quem depura,
     * senão uma falha client-side vira uma operação aparentemente bem-sucedida no relato. */
    agentMessage?: string;
    traceId: string;
  },
) {
  // eslint-disable-next-line no-console
  console.error(`[argink:${source}]`, info);
}

/**
 * **`Error.message` NÃO é copy de superfície (§3/§23).** As duas classes de erro deste app
 * geram a própria mensagem quando o servidor não manda uma: `ApiError` monta
 * `"erro da API (HTTP 500)"` e `AgentError` monta coisas como `"agente local respondeu sem
 * nonce de autorização"` — e `nonce` está em `FORBIDDEN_SURFACE_TERMS`. Aquelas strings são
 * boas onde nasceram, no `Error` que vai para o log e para a stack de quem depura; o defeito
 * era `describeError` usá-las como `detail`, que é texto de TELA. O caminho não era exótico:
 * bastava o servidor responder sem `message`, que é o normal em 5xx e em falha de rede.
 *
 * A separação passa a ser estrutural, não vigilância: a superfície lê `err.body.message` (o
 * que o SERVIDOR escreveu para o usuário) ou uma destas três frases. `err.message` continua
 * inteiro no `Error` e o `traceId` continua na tela — quem investiga não perdeu nada.
 */
const MENSAGEM_DE_REDE =
  'não foi possível falar com o ARGINK — verifique sua conexão e tente novamente';
const MENSAGEM_GENERICA =
  'algo deu errado do nosso lado. Tente novamente; se continuar, informe o código de suporte abaixo';
const MENSAGEM_DO_ASSISTENTE =
  'o ARGINK Bridge não aceitou a operação neste computador — feche e abra o programa e tente de novo';

/**
 * Conduta por causa, derivada do status que o agente devolveu. Sem isto, três situações que
 * pedem ações diferentes recebiam a mesma instrução inútil.
 */
/**
 * Aceita o valor só quando ele tem FORMA de código: minúsculas, dígitos e `_`, curto. Texto
 * livre — e uma mensagem de exceção sempre é — não passa. Fail-closed: na dúvida, `null`.
 */
function codigoSeguro(valor: string | undefined): string | null {
  if (!valor) return null;
  return /^[a-z][a-z0-9_]{0,63}$/.test(valor) ? valor : null;
}

function remediacaoDoAssistente(status: number | null): string {
  switch (status) {
    case 404:
      return 'o certificado escolhido não está mais disponível nesta máquina — atualize a lista e escolha outro';
    case 422:
      return 'este certificado não serve para assinar aqui — escolha outro na lista';
    case 401:
    case 403:
      return 'a confirmação expirou ou foi negada — refaça a confirmação na janela que o computador abre e tente de novo';
    default:
      return MENSAGEM_DO_ASSISTENTE;
  }
}

/**
 * **A `message` da API é escrita para INTEGRADOR, não para o signatário** (P1-2, 2ª passada
 * da auditoria). As rotas `/v1/signers/*` servem os dois públicos com o mesmo corpo, e ali o
 * texto é — corretamente, para quem integra — técnico: "envelope vencido (expires_at)", "o
 * stage deste signatário ainda não está disponível", "somente para documentos PDF/PAdES",
 * "cert_chain é obrigatório". Tudo isso chegava à tela por `err.body?.message`, vencendo o
 * fallback local, e nenhum gate deste app o alcançava: o gate mede o que o CLIENTE escreve.
 *
 * O `error` CODE é contrato estável; a `message` não é. Então a copy passa a vir daqui para
 * os códigos que essas rotas emitem, e `body.message` fica só para código desconhecido.
 *
 * **Lacuna que permanece, declarada:** um código NOVO na API continua trazendo a mensagem do
 * servidor para a tela. Fechar isso de vez exige separar `message` (integrador) de uma copy
 * de usuário no próprio contrato — frente da API, fora do M6. Registrado como débito, não
 * como resolvido.
 */
const COPY_POR_CODIGO: Record<string, HumanError['detail']> = {
  envelope_expired: 'o prazo para assinar este documento terminou',
  stage_not_available: 'ainda não é a sua vez — os participantes anteriores precisam assinar primeiro',
  conflict: 'o estado deste documento mudou enquanto você estava aqui — recarregue a página',
  simple_format_unsupported: 'este documento não está num formato que a assinatura simples aceita',
  validation_error: 'os dados enviados não foram aceitos',
  observer_cannot_sign: 'você foi incluído para acompanhar este documento, não para assiná-lo',
  channel_not_verified: 'confirme o código enviado para o seu e-mail antes de assinar',
  consent_missing: 'é preciso aceitar a declaração antes de assinar',
  session_expired: 'sua sessão de assinatura expirou — abra o link de novo',
  signer_token_unavailable: 'este link de assinatura não está mais válido',
  document_revision_stale: 'o documento mudou desde que você abriu esta página — recarregue',
  placement_moved: 'a posição da sua assinatura mudou — confira antes de continuar',
  not_found: 'este link não corresponde a um documento válido',
  /* A API responde com "POST /session" no texto — instrução de INTEGRADOR, inútil para quem
     assina. Achado da 3ª passada da auditoria: o código já existe hoje, então a lacuna do
     mapa não era só sobre códigos futuros. */
  completion_failed_restart_required:
    'a assinatura não pôde ser concluída e precisa ser refeita — abra o link de novo e assine outra vez',
  simple_signature_unavailable: 'a assinatura simples está indisponível no momento — tente de novo em instantes',
  observer_cannot_sign_alt: 'você foi incluído para acompanhar este documento, não para assiná-lo',
};

/** Copy de usuário para o código, quando conhecido. Nunca a `message` do servidor por cima. */
function detalhePorCodigo(err: ApiError): string | null {
  const code = err.body?.error;
  return (code && COPY_POR_CODIGO[code]) || null;
}

export function describeError(err: unknown): HumanError {
  if (err instanceof ApiError) {
    const traceId = err.body?.trace_id ?? err.traceId;
    logTechnical('api', {
      status: err.status,
      errCode: err.body?.error ?? null,
      errClass: err.body?.err_class ?? null,
      traceId,
    });

    const d034 = describeD034Error(err, traceId);
    if (d034) return d034;

    /* Código conhecido ganha copy de usuário e nunca cede para `body.message`. */
    const porCodigo = detalhePorCodigo(err);

    switch (err.status) {
      case 0:
        // status 0 cobre timeout/rede/token ausente — a mensagem específica já
        // vem pronta em `err.body.message` (montada em src/lib/api.ts conforme
        // o `error` sintético: 'timeout' | 'network_error' | 'missing_session_token'
        // | 'api_unreachable').
        return { title: 'Sem conexão com o ARGINK', detail: porCodigo ?? err.body?.message ?? MENSAGEM_DE_REDE, traceId };
      case 400:
        return { title: 'Dados inválidos', detail: porCodigo ?? err.body?.message ?? 'os dados enviados não foram aceitos', traceId };
      case 401:
        return {
          title: 'Este link não vale mais',
          // O signatário não tem "credenciais": ele tem um LINK, e é sobre ele que
          // precisa saber o que fazer. A conduta ("peça um novo") é a parte útil.
          detail:
            porCodigo ??
            err.body?.message ??
            'o link de assinatura expirou ou já foi usado — peça um novo a quem enviou o documento',
          traceId,
        };
      case 402:
        return {
          title: 'Cota do plano esgotada',
          detail: porCodigo ?? err.body?.message ?? 'o limite de assinaturas do remetente foi atingido — fale com quem enviou o documento',
          traceId,
        };
      case 404:
        return { title: 'Não encontrado', detail: porCodigo ?? err.body?.message ?? 'este link não corresponde a um documento válido', traceId };
      case 409:
        // Débito #19 — conclusão em voo (lock `completing` da API): não é "fora de ordem",
        // é só "aguarde": a própria assinatura deste signatário está sendo processada agora.
        if (err.body?.error === 'completion_in_progress') {
          return {
            title: 'Assinatura em processamento',
            detail:
              'Sua assinatura já está sendo processada. Aguarde alguns instantes e tente novamente — ela não será gerada em duplicidade.',
            traceId,
          };
        }
        return {
          title: 'Fora de ordem',
          detail:
            porCodigo ??
            err.body?.message ??
            'o estado do documento mudou (outra aba, outro signatário, ou a cerimônia já avançou) — recarregue a página',
          traceId,
        };
      case 410:
        return {
          // §23/§54: "envelope" é CONTRATO (banco, DTO, rota), nunca superfície — aqui
          // era a única tela do produto que ainda o mostrava ao usuário, e mostrava
          // justamente a quem não tem contexto nenhum para decifrá-lo.
          title: 'Prazo encerrado',
          detail: porCodigo ?? err.body?.message ?? 'o prazo para assinar este documento terminou',
          traceId,
        };
      case 501:
        return { title: 'Ainda não suportado', detail: porCodigo ?? err.body?.message ?? 'este recurso ainda não está disponível', traceId };
      case 502:
        return { title: 'Motor de assinatura indisponível', detail: err.body?.message ?? 'tente novamente em instantes', traceId };
      case 503:
        return { title: 'Serviço indisponível', detail: err.body?.message ?? 'tente novamente em instantes', traceId };
      default:
        // O código HTTP não sai da tela por vaidade de limpeza: ele continua em
        // `logTechnical` acima e o código de suporte (`traceId`) segue visível — quem
        // investiga tem os dois. Quem só quer assinar não tinha o que fazer com ele.
        return { title: 'Não foi possível concluir', detail: porCodigo ?? err.body?.message ?? MENSAGEM_GENERICA, traceId };
    }
  }
  if (err instanceof AgentError) {
    const traceId = err.body?.trace_id ?? err.traceId;
    logTechnical('agent', {
      status: err.status,
      kind: err.kind,
      errClass: err.body?.err_class ?? null,
      /* A mensagem do agente saiu da TELA, não do diagnóstico (P1-3): sem ela aqui, uma
         falha client-side — resposta 200 sem nonce, sem valor de assinatura — chegaria ao
         suporte como uma operação que o agente considera bem-sucedida. */
      agentMessage: err.message,
      /* E o código que o AGENTE classificou — SANITIZADO.
       *
       * O 500 do agente não devolve um código: devolve `e.getClass().getSimpleName() + ": " +
       * e.getMessage()` (`apps/local-agent/.../App.java`). E o próprio arquivo registra, três
       * linhas acima, que `e.getMessage()` pode carregar um trecho do payload — Jackson inclui
       * um recorte do source na exceção de parse, e o payload leva `dtbs_b64`/`nonce`. Por isso
       * ele evita a mensagem no LOG do agente; o JSON, não.
       *
       * Registrar esse campo cru aqui contrariava a garantia declarada em `logTechnical` de que
       * o log nunca inclui DTBS nem payload — e a auditoria adversarial pegou. `codigoSeguro`
       * deixa passar só o que TEM forma de código; qualquer coisa com espaço, dois-pontos ou
       * comprimento de frase é descartada, e o `agentMessage` (construído por ESTE cliente,
       * nunca pelo servidor) continua dando o contexto de diagnóstico. */
      errCode: codigoSeguro(err.body?.error),
      traceId,
    });
    switch (err.kind) {
      case 'agent_unreachable':
        return {
          title: 'O ARGINK Bridge não está disponível neste computador',
          detail:
            'Não foi possível falar com o ARGINK Bridge nesta máquina. Confira se ele está instalado e clique em "Tentar de novo".',
          traceId,
        };
      case 'timeout':
        // Distinto do timeout da API (case 0 acima): aqui quem não respondeu foi
        // o agente local, não o servidor.
        return {
          title: 'O ARGINK Bridge demorou demais para responder',
          detail:
            'O ARGINK Bridge nesta máquina não respondeu a tempo. Tente novamente em instantes.',
          traceId,
        };
      case 'origin_blocked':
        return {
          title: 'Origem não autorizada',
          detail:
            err.body?.message ??
            'esta página não está autorizada a falar com o ARGINK Bridge deste computador — isto não é sessão expirada.',
          traceId,
        };
      case 'consent_timeout':
        /* O diálogo nativo ABRIU e ninguém respondeu. Isto NÃO é `timeout` (acima): ali quem
         * falhou foi a máquina; aqui a máquina fez a parte dela e ficou esperando uma pessoa.
         * Medido no Gate B de 09/09/2026: a tela dizia "o ARGINK Bridge não respondeu a tempo"
         * enquanto a janela do Windows estava aberta atrás do navegador — o texto acusava o
         * programa e escondia a única ação que destrava a tela. */
        return {
          title: 'A janela de autorização não foi respondida',
          detail:
            'O ARGINK Bridge abriu uma janela do Windows pedindo sua autorização e ela expirou sem resposta. Ela costuma aparecer ATRÁS do navegador. Clique em "Tentar de novo" e responda à janela que abrir.',
          traceId,
        };
      case 'auth_denied':
        return {
          title: 'Autorização expirada ou negada',
          detail:
            err.body?.message ??
            'a confirmação de assinatura expirou ou foi negada — refaça a confirmação na janela que o computador abre e tente de novo.',
          traceId,
        };
      case 'response':
      default:
        /* **P1-3 da auditoria adversarial: a separação apagou a CAUSA.**
         *
         * Trocar `err.message` por uma frase única resolveu o vazamento de jargão
         * ("sem nonce de autorização") e criou outro problema no lugar: certificado removido
         * (404) e algoritmo não suportado (422) passaram a dizer a MESMA coisa — "feche e
         * abra o aplicativo" —, que não resolve nenhum dos dois. Quem perdeu o certificado
         * precisa escolher outro; quem tem um certificado incompatível precisa saber que é
         * incompatível. Fechar e abrir não muda nem um nem outro.
         *
         * O status HTTP do agente é a informação estruturada que já chega aqui, e é ela que
         * decide a conduta — não o texto, que continua fora da tela. */
        return {
          title: 'O ARGINK Bridge recusou a operação',
          detail: err.body?.message ?? remediacaoDoAssistente(err.status),
          traceId,
        };
    }
  }
  if (err instanceof Error) {
    const traceId = newTraceId();
    logTechnical('unknown', { status: null, errClass: null, traceId });
    return { title: 'Falha inesperada', detail: MENSAGEM_GENERICA, traceId };
  }
  return { title: 'Falha inesperada', detail: MENSAGEM_GENERICA, traceId: newTraceId() };
}

export type SimpleFlowOperation = 'request' | 'verify' | 'consent' | 'complete';

/**
 * Mensagens específicas do fluxo Simples (OTP por e-mail) que `describeError`
 * (genérico por status HTTP) não cobre — reenvio limitado, canal indisponível,
 * código inválido/expirado, tentativas esgotadas. Cai no `describeError` padrão
 * para qualquer outro caso. Extraído de `SignPage.tsx` para ser testável sem
 * montar o componente (ver `errors.test.ts`).
 */
export function specificSimpleError(err: unknown, operation: SimpleFlowOperation): HumanError {
  if (err instanceof ApiError) {
    const traceId = err.body?.trace_id ?? err.traceId;
    const code = err.body?.error;
    if (operation === 'request' && err.status === 429) {
      return {
        title: 'Aguarde antes de reenviar',
        detail: 'Muitas solicitações de código foram feitas. Aguarde alguns minutos e tente novamente.',
        traceId,
      };
    }
    if (operation === 'request' && err.status === 503) {
      return {
        title: 'Canal de e-mail indisponível',
        detail: 'Não foi possível entregar o código. A assinatura não continuará sem verificar o canal.',
        traceId,
      };
    }
    if (operation === 'verify' && err.status === 422 && code === 'invalid_otp') {
      return {
        title: 'Código inválido ou expirado',
        detail: 'Confira os 6 dígitos ou solicite um novo código.',
        traceId,
      };
    }
    if (operation === 'verify' && err.status === 429 && code === 'otp_attempts_exhausted') {
      return {
        title: 'Tentativas esgotadas',
        detail: 'Solicite um novo código para continuar.',
        traceId,
      };
    }
  }
  return describeError(err);
}
